import { CompleteLicense } from '@/interfaces/dashboard/licenses';
import { Person } from '@/interfaces/person';
import { Suspend } from '@/interfaces/search/search.interface';
import { SearchService } from '@/services/search/search.service';
import { ofuscate } from '@/utils/obfuscation';
import { dateEndMustBeAfterDateStart } from '@/validators/dateEndMustBeAfterDateStart';
import { dateEndSettedOrUndefined } from '@/validators/dateEndSettedOrUndefined';
import { dateMustBeAfterOrEqualThanToday } from '@/validators/dateMustBeAfterOrEqualThanToday';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { StoreState } from '@/store';
import { Store } from '@ngrx/store';
import { loaderAnimationShow, loaderAnimationHide } from '@/store/page-loader/action';

@Component({
  selector: 'app-suspension-modal',
  templateUrl: './suspension-modal.component.html',
  styleUrls: ['./suspension-modal.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuspensionModalComponent implements OnInit {

  @Input() visible = true;
  @Input() licenseId: number = 0;
  @Input() userId: string = '';
  @Output() handleClose: EventEmitter<void> = new EventEmitter();
  @Input() licenseSelected?: CompleteLicense;
  @Input() personData!: Person;

  suspensionForm: FormGroup = new FormGroup({});
  constructor(
    private searchService: SearchService,
    private router: Router,
    public route: ActivatedRoute,
    private store: Store<StoreState>,
  ) {

  }

  ngOnInit() {
    this.suspensionForm = new FormGroup({
      licenseId: new FormControl(this.licenseId),
      userId: new FormControl(this.userId),
      reason: new FormControl('', [Validators.minLength(5), Validators.required]),
      suspensionRange: new FormGroup({
        sinceDate: new FormControl(null, [Validators.required]),
        untilDate: new FormControl(null, [dateEndSettedOrUndefined(), dateMustBeAfterOrEqualThanToday()]),
      }, [dateEndMustBeAfterDateStart('sinceDate', 'untilDate')]),
    });
  }

  asFormControl(abstractControl: AbstractControl): FormControl {
    return abstractControl as FormControl;
  }

  get suspensionSinceDate(): FormControl {
    const suspensionRangeForm = this.suspensionForm.controls.suspensionRange as FormGroup;
    return suspensionRangeForm.controls.sinceDate as FormControl;
  }
  get suspensionUntilDate(): FormControl {
    const suspensionRangeForm = this.suspensionForm.controls.suspensionRange as FormGroup;
    return suspensionRangeForm.controls.untilDate as FormControl;
  }
  get controlSuspensionRangeStartError(): string {
    const sanctionRangeForm = this.suspensionForm.controls.suspensionRange as FormGroup;
    const error = sanctionRangeForm.controls.sinceDate.errors;
    if (error) {
      if (error.description) {
        return error.description;
      }
    }
    return '';
  }
  get controlSuspensionRangeEndError(): string {
    const suspensionRangeForm = this.suspensionForm.controls.suspensionRange as FormGroup;
    const errorEnd = suspensionRangeForm.controls.untilDate.errors;
    const errorRange = suspensionRangeForm.errors;

    const currentError = errorEnd || errorRange;

    if (currentError) {
      if (currentError.description) {
        return currentError.description;
      }
    }
    return '';
  }

  onConfirmSuspension() {
    const { licenseId, reason, suspensionRange, userId } = this.suspensionForm.value;
    const formDataMapped: Suspend = {
      licenseId,
      suspensionReason: reason,
      suspensionDateFromAt: suspensionRange.sinceDate,
      suspensionDateToAt: suspensionRange.untilDate,
      suspensionMdmid: userId,
    };

    const currentOfuscatedData = this.router.url.split('/').reverse()[1];
    // primer dispatch
    this.store.dispatch(loaderAnimationShow());
    this.searchService.onSuspendLicense(formDataMapped)
      .subscribe({
        next: () => {
          const ofuscatedData = ofuscate({
            title: 'Suspender licencia',
            subtitle: `La licencia ${this.licenseSelected?.asset.name} ha sido suspendida exitosamente.`,
            message: `Una copia de la suspensión fue enviada al
            correo electrónico ${this.personData.email}`,
            messageBold: `N° registro: ${this.licenseId}`,
            id: formDataMapped.licenseId,
            actionButtonPath: `/search/driver/result/${currentOfuscatedData}/licenses`,
            actionButtonText: 'Ir al perfil de conductor',
            variant: 'success',
          });
          this.router.navigateByUrl(`/search/action/result/${ofuscatedData}`);
        },
        error: () => {
          this.store.dispatch(loaderAnimationHide());

          this.store.select('pageError').subscribe({
            next: (pageConfig) => {
              const [website, ...url] = window.location.href.split('//')[1].split('/');
              if (pageConfig.errorType !== 'Mulesoft') {
                const ofuscatedData = ofuscate({
                  title: 'Suspender licencia',
                  subtitle: 'Hubo un problema y no hemos podido suspender la licencia. Por favor intenta de nuevo.',
                  actionButtonPath: `/search/driver/result/${currentOfuscatedData}/licenses`,
                  actionButtonText: 'Ir al perfil de conductor',
                  variant: 'failure',
                });
                this.router.navigateByUrl(`/search/action/result/${ofuscatedData}`);
              }
            },
          });

        },
        complete: () => {
          // segundo dispatch
          this.store.dispatch(loaderAnimationHide());
        },
      });
  }

}
